import { Link } from 'react-router-dom';
import { ADMIN_NAVIGATION, NAVIGATION, type NavItem } from '../components/layout/navigation';
import { PageHeader } from '../components/ui/Card';
import { useDashboardStats } from '../hooks/useApi';
import { t, tr } from '../i18n';
import { useAuthStore, useIsAdmin } from '../store';
import type { DashboardStats } from '../types';
import { cn, formatNumber } from '../utils/helpers';

/** Number shown on a tile; `alert` counts are things needing attention (red) and are hidden when zero. */
const TILE_COUNTS: Record<string, { key: keyof DashboardStats; alert?: boolean }> = {
  '/cars': { key: 'total_cars' },
  '/drivers': { key: 'total_drivers' },
  '/assignments': { key: 'active_assignments' },
  '/maintenance': { key: 'upcoming_services', alert: true },
  '/documents': { key: 'expiring_documents', alert: true },
  '/vignettes': { key: 'expiring_vignettes', alert: true },
  '/fines': { key: 'unpaid_fines', alert: true },
};

const COUNT_TITLES: Record<string, string> = tr({
  '/cars': 'Vozidlá v evidencii',
  '/drivers': 'Aktívni vodiči',
  '/assignments': 'Požičané vozidlá',
  '/maintenance': 'Servisy do 30 dní',
  '/documents': 'Končiace dokumenty',
  '/vignettes': 'Známky končiace do 30 dní',
  '/fines': 'Nezaplatené pokuty',
});

function Tile({ item, stats }: { item: NavItem; stats?: DashboardStats }) {
  const count = TILE_COUNTS[item.href];
  const value = count && stats ? (stats[count.key] as number) : undefined;
  const showCount = value !== undefined && (!count?.alert || value > 0);

  return (
    <Link
      to={item.href}
      className="group relative flex aspect-square flex-col items-center justify-center gap-3 rounded-2xl border border-secondary-200 bg-white p-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-600 group-hover:text-white">
        <item.icon className="h-7 w-7" aria-hidden="true" />
      </span>
      <span className="text-sm font-semibold text-secondary-900">{item.name}</span>
      {showCount && (
        <span
          className={cn(
            'absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
            count?.alert ? 'bg-red-100 text-red-700' : 'bg-secondary-100 text-secondary-600',
          )}
          title={`${COUNT_TITLES[item.href]}: ${formatNumber(value)}`}
        >
          {formatNumber(value)}
        </span>
      )}
      {item.upcoming && (
        <span className="absolute right-2 top-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          {t('Čoskoro')}
        </span>
      )}
    </Link>
  );
}

/** Home page of the tile view: every section as a big icon. */
export function TileHome() {
  const isAdmin = useIsAdmin();
  const user = useAuthStore((s) => s.user);
  const { data: stats } = useDashboardStats();
  // The overview itself moves to its own page in the tile view
  const items = NAVIGATION().map((item) => (item.href === '/' ? { ...item, href: '/overview', end: false } : item));
  const grid = 'grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6';

  return (
    <>
      <PageHeader title={t('Vitajte, {name}', { name: user?.full_name || user?.username })} subtitle={t('Vyberte, s čím chcete pracovať')} />
      <div className={grid}>
        {items.map((item) => (
          <Tile key={item.href} item={item} stats={stats} />
        ))}
      </div>
      {isAdmin && (
        <>
          <h2 className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wider text-secondary-400">{t('Administrácia')}</h2>
          <div className={grid}>
            {ADMIN_NAVIGATION().map((item) => (
              <Tile key={item.href} item={item} />
            ))}
          </div>
        </>
      )}
    </>
  );
}
